import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { DataTable } from "@/components/DataTable";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { Trophy, PlusCircle, Users2, TrendingUp, Bell, ShieldAlert } from "lucide-react";
import LatexText from "@/components/LatexText";

export default function ExamAdminPage() {
  const [tests, setTests] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [qb, setQb] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", description: "", instructions: "",
    type: "Mock", duration_min: 60, negative_marking: true,
    shuffle_questions: true, shuffle_options: true, allow_calculator: false,
    auto_submit: true, multiple_attempts: false, proctoring_enabled: true,
    passing_marks: 0, batches: [],
    start_at: new Date(Date.now() - 5*60000).toISOString().slice(0,16),
    end_at: new Date(Date.now() + 24*3600*1000).toISOString().slice(0,16),
    sections: [{ name: "Section 1", subject: "", duration_min: 0, question_ids: [] }],
  });

  const load = async () => {
    const [{data:t}, {data:a}, {data:q}] = await Promise.all([
      api.get("/exams/tests"), api.get("/exams/admin/analytics"), api.get("/qb/questions"),
    ]);
    setTests(t); setAnalytics(a); setQb(q);
  };
  useEffect(()=>{load()},[]);

  const toggleQuestion = (secIdx, qid) => {
    const sections = [...form.sections];
    const cur = sections[secIdx].question_ids;
    sections[secIdx] = { ...sections[secIdx], question_ids: cur.includes(qid) ? cur.filter(x => x !== qid) : [...cur, qid] };
    setForm({...form, sections});
  };

  const addSection = () => setForm({...form, sections: [...form.sections, { name: `Section ${form.sections.length+1}`, subject: "", duration_min: 0, question_ids: [] }]});
  const removeSection = (i) => setForm({...form, sections: form.sections.filter((_, idx) => idx !== i)});

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      duration_min: Number(form.duration_min),
      passing_marks: Number(form.passing_marks) || 0,
      start_at: new Date(form.start_at).toISOString(),
      end_at: new Date(form.end_at).toISOString(),
    };
    try {
      await api.post("/exams/tests", payload);
      toast.success("Exam test created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const totalQuestions = form.sections.reduce((s, x) => s + x.question_ids.length, 0);

  return (
    <div>
      <PageHeader title="Exam Admin" subtitle="Create tests, view analytics, and monitor proctoring"
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button className="btn-primary inline-flex items-center gap-2" data-testid="new-exam-test-btn"><PlusCircle size={14}/> New exam test</button></DialogTrigger>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create exam test</DialogTitle></DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <input required placeholder="Test name" data-testid="et-name-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.name} onChange={e=>setForm({...form, name:e.target.value})}/>
                  <input placeholder="Code (e.g. MOCK-JEE-02)" data-testid="et-code-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.code} onChange={e=>setForm({...form, code:e.target.value})}/>
                </div>
                <textarea placeholder="Description / instructions" data-testid="et-desc-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md min-h-[70px]" value={form.description} onChange={e=>setForm({...form, description:e.target.value})}/>
                <div className="grid grid-cols-4 gap-3">
                  <select data-testid="et-type-select" className="px-3 py-2 border border-[#E0DFDC] rounded-md bg-white" value={form.type} onChange={e=>setForm({...form, type:e.target.value})}>
                    {["Mock","Weekly","Monthly","Daily","Chapter","Subject","Full","Scholarship","Live","Practice","PYP","Adaptive"].map(x=><option key={x}>{x}</option>)}
                  </select>
                  <input type="number" placeholder="Duration (min)" data-testid="et-duration-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.duration_min} onChange={e=>setForm({...form, duration_min:e.target.value})}/>
                  <input type="number" placeholder="Passing marks" data-testid="et-passing-input" className="px-3 py-2 border border-[#E0DFDC] rounded-md" value={form.passing_marks} onChange={e=>setForm({...form, passing_marks:e.target.value})}/>
                  <div className="text-xs text-[#00000099] flex items-center">
                    <span className="font-semibold text-[#000000E6]">{totalQuestions}</span>&nbsp;questions selected
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-[#00000099] uppercase tracking-wider">Start</label>
                    <input type="datetime-local" data-testid="et-start-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md mt-1" value={form.start_at} onChange={e=>setForm({...form, start_at:e.target.value})}/>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-[#00000099] uppercase tracking-wider">End</label>
                    <input type="datetime-local" data-testid="et-end-input" className="w-full px-3 py-2 border border-[#E0DFDC] rounded-md mt-1" value={form.end_at} onChange={e=>setForm({...form, end_at:e.target.value})}/>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm">
                  {[["negative_marking","Negative marking"],["shuffle_questions","Shuffle questions"],["shuffle_options","Shuffle options"],["allow_calculator","Calculator"],["auto_submit","Auto submit"],["proctoring_enabled","Proctoring"]].map(([k, label]) => (
                    <label key={k} className="flex items-center gap-2" data-testid={`et-opt-${k}`}>
                      <input type="checkbox" checked={!!form[k]} onChange={e=>setForm({...form, [k]:e.target.checked})} className="accent-[#0A66C2]"/> {label}
                    </label>
                  ))}
                </div>

                {/* Sections */}
                <div className="pt-4 border-t border-[#E0DFDC]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold">Sections & Questions</div>
                    <button type="button" onClick={addSection} data-testid="add-section-btn" className="text-xs px-3 py-1 rounded-full bg-[#EDEBE7] hover:bg-[#E0DFDC] font-semibold">+ Section</button>
                  </div>
                  {form.sections.map((s, si) => (
                    <div key={si} className="p-3 border border-[#E0DFDC] rounded-md mb-3" data-testid={`section-${si}`}>
                      <div className="grid grid-cols-4 gap-2 mb-2">
                        <input placeholder="Name" data-testid={`sec-${si}-name`} className="px-2 py-1 border border-[#E0DFDC] rounded text-sm" value={s.name} onChange={e=>{ const n=[...form.sections]; n[si]={...n[si], name:e.target.value}; setForm({...form, sections:n}); }}/>
                        <input placeholder="Subject filter" data-testid={`sec-${si}-subject`} className="px-2 py-1 border border-[#E0DFDC] rounded text-sm" value={s.subject} onChange={e=>{ const n=[...form.sections]; n[si]={...n[si], subject:e.target.value}; setForm({...form, sections:n}); }}/>
                        <input type="number" placeholder="Section duration (min, 0=uses overall)" data-testid={`sec-${si}-dur`} className="px-2 py-1 border border-[#E0DFDC] rounded text-sm" value={s.duration_min} onChange={e=>{ const n=[...form.sections]; n[si]={...n[si], duration_min:Number(e.target.value)||0}; setForm({...form, sections:n}); }}/>
                        {form.sections.length > 1 && <button type="button" onClick={()=>removeSection(si)} className="text-xs text-[#CC1016]" data-testid={`remove-sec-${si}`}>Remove</button>}
                      </div>
                      <div className="max-h-56 overflow-y-auto border border-[#E0DFDC] rounded p-2">
                        {qb.filter(q => !s.subject || q.subject.toLowerCase().includes(s.subject.toLowerCase())).map(q => (
                          <label key={q.id} className="flex items-start gap-2 py-1.5 border-b border-[#F4F2EE] last:border-b-0 cursor-pointer">
                            <input type="checkbox" checked={s.question_ids.includes(q.id)} onChange={()=>toggleQuestion(si, q.id)} className="mt-1 accent-[#0A66C2]" data-testid={`q-pick-${si}-${q.id}`}/>
                            <div className="text-xs flex-1">
                              <div className="text-[10px] uppercase text-[#00000099]">{q.subject} · {q.difficulty} · {q.type} · {q.marks}m</div>
                              <div className="text-[#000000E6]"><LatexText>{q.text}</LatexText></div>
                            </div>
                          </label>
                        ))}
                      </div>
                      <div className="mt-1 text-[11px] text-[#00000099]">{s.question_ids.length} question(s) in this section</div>
                    </div>
                  ))}
                </div>

                <DialogFooter><button type="submit" className="btn-primary" data-testid="et-save-btn">Create test</button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <Tabs defaultValue="analytics" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="tests" data-testid="tab-tests">Tests</TabsTrigger>
          <TabsTrigger value="incidents" data-testid="tab-incidents">Proctoring incidents</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics">
          {analytics && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                <Kpi label="Total tests" value={analytics.total_tests}/>
                <Kpi label="Scheduled" value={analytics.scheduled_tests} color="#915907"/>
                <Kpi label="Submitted" value={analytics.submitted} color="#057642"/>
                <Kpi label="In progress" value={analytics.in_progress} color="#0A66C2"/>
                <Kpi label="Avg %" value={`${analytics.avg_percent}%`} color="#5A2A82"/>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="linkedin-card p-5">
                  <h3 className="font-semibold mb-3 inline-flex items-center gap-2"><Trophy size={16} className="text-[#915907]"/> Top performers</h3>
                  <div className="divide-y divide-[#E0DFDC]" data-testid="admin-top-performers">
                    {analytics.top_performers.length === 0 && <div className="text-sm text-[#00000099] py-4">No submissions yet</div>}
                    {analytics.top_performers.map((r, i) => (
                      <div key={i} className="py-2 flex justify-between">
                        <div>
                          <div className="text-sm font-semibold">{r.student_name}</div>
                          <div className="text-[11px] text-[#00000099]">{r.test_name}</div>
                        </div>
                        <div className="text-sm font-bold text-[#0A66C2]">{r.percent}%</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="linkedin-card p-5">
                  <h3 className="font-semibold mb-3">Per-test averages</h3>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.per_test}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC"/>
                      <XAxis dataKey="test_name" stroke="#00000099" tick={{fontSize: 10}}/>
                      <YAxis stroke="#00000099" tick={{fontSize: 12}}/>
                      <Tooltip contentStyle={{background:"#fff", border:"1px solid #E0DFDC", borderRadius:8}}/>
                      <Bar dataKey="avg" name="Avg %" fill="#0A66C2" radius={[6,6,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="tests">
          <DataTable
            testid="exam-tests-table"
            columns={[
              { key: "name", label: "Test" },
              { key: "type", label: "Type" },
              { key: "status", label: "Status", render: r => (
                <span className="text-[11px] uppercase font-semibold px-2 py-0.5 rounded" style={{
                  background: {live:"#EAF3FB",scheduled:"#FFF3CD",closed:"#F8D7DA"}[r.status], 
                  color: {live:"#0A66C2",scheduled:"#915907",closed:"#CC1016"}[r.status]
                }}>{r.status}</span>
              )},
              { key: "start_at", label: "Start", render: r => new Date(r.start_at).toLocaleString() },
              { key: "duration_min", label: "Duration", render: r => `${r.duration_min} min` },
              { key: "question_count", label: "Questions" },
              { key: "total_marks", label: "Marks" },
              { key: "proctoring_enabled", label: "Proctoring", render: r => r.proctoring_enabled ? <ShieldAlert size={14} className="text-[#0A66C2]"/> : "—" },
            ]}
            rows={tests}
          />
        </TabsContent>

        <TabsContent value="incidents">
          {analytics && (
            <div className="linkedin-card p-5">
              <h3 className="font-semibold mb-3">Incidents by type</h3>
              {Object.keys(analytics.incidents_by_type).length === 0 ? (
                <div className="text-sm text-[#00000099] py-4">No incidents yet</div>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={Object.entries(analytics.incidents_by_type).map(([k,v])=>({name:k, count:v}))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E0DFDC"/>
                    <XAxis dataKey="name" stroke="#00000099" tick={{fontSize: 11}}/>
                    <YAxis stroke="#00000099" tick={{fontSize: 12}}/>
                    <Tooltip contentStyle={{background:"#fff", border:"1px solid #E0DFDC", borderRadius:8}}/>
                    <Bar dataKey="count" fill="#CC1016" radius={[6,6,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Kpi({ label, value, color = "#0A66C2" }) {
  return (
    <div className="linkedin-card p-3" data-testid={`admin-kpi-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="kpi-label">{label}</div>
      <div className="text-xl font-bold mt-1" style={{color}}>{value}</div>
    </div>
  );
}
